import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { P, type ID } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { useDebounced } from '../../../lib/hooks';
import { useGo } from '../../../navigation/nav';
import { ProfileDetails } from '../../ess/screens/MyProfile';
import { Avatar, Button, Card, EmptyState, Grid, Loading, Modal, PageHeader, Screen, StatusBadge, Text, TextField } from '../../../ui';

/** Cards for each direct / indirect reportee with a profile sheet. */
export function TeamProfilesScreen() {
  const go = useGo();
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const dq = useDebounced(search);
  const [sel, setSel] = useState<ID | null>(null);
  const q = useQuery({ queryKey: ['employees', 'team', dq], queryFn: () => api.employees.list({ scope: 'team', search: dq || undefined, per_page: 100 }) });
  const one = useQuery({ queryKey: ['employee', sel], queryFn: () => api.employees.get(sel!), enabled: !!sel });
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="team-profiles">
      <PageHeader title="Team profiles" subtitle={q.data ? `${q.data.total} people` : undefined} />
      <TextField placeholder="Search team…" accessibilityLabel="Search team" value={search} onChangeText={setSearch} containerStyle={{ maxWidth: 360 }} />
      {q.isLoading ? (
        <Loading />
      ) : !q.data?.data.length ? (
        <Card>
          <EmptyState icon="users" title="No team members found" />
        </Card>
      ) : (
        <Grid min={200}>
          {q.data.data.map((e) => (
            <Card key={e.id} onPress={() => setSel(e.id)}>
              <View style={{ alignItems: 'center', gap: 6 }}>
                <Avatar name={e.full_name} size={56} />
                <Text weight="semibold" align="center" numberOfLines={1}>{e.full_name}</Text>
                <Text variant="small" align="center" numberOfLines={1}>{e.designation ?? '—'}</Text>
                <Text variant="small" align="center" numberOfLines={1}>{e.email}</Text>
                <StatusBadge status={e.status} />
              </View>
            </Card>
          ))}
        </Grid>
      )}
      <Modal
        open={!!sel}
        onClose={() => setSel(null)}
        title={one.data?.full_name ?? 'Profile'}
        subtitle={one.data ? `${one.data.emp_code} · ${one.data.designation ?? ''}` : undefined}
        width={680}
        footer={can(P.EMPLOYEES_VIEW, P.EMPLOYEES_MANAGE) && sel ? <Button title="Open full record" iconRight="chevronRight" onPress={() => { const id = sel; setSel(null); go('EmployeeDetail', { id }); }} /> : undefined}
      >
        {one.data ? <ProfileDetails emp={one.data} /> : <Loading />}
      </Modal>
    </Screen>
  );
}
